/**
 * ============================================================================
 * DEMAND CATEGORY REGISTRY — single source of truth for the WBR taxonomy.
 * ============================================================================
 *
 * Terminology and names match the EXISTING WBR QuickSight dashboard
 * (CollectionTrackerServiceCDK quicksightStack.ts `DEMAND_CATEGORIES`, backed
 * by the `demand_category` column). We do not invent new vocabulary.
 *
 * Adding/renaming an entry = editing THIS array only; the checkbox row,
 * display names, defaults, and data matching all derive from it. The
 * registry-coverage unit test keeps it in sync with the NetworkId slug type
 * (the slugs are pre-existing URL params — /network/:id — kept for link
 * stability).
 *
 * `values`: the real DcrRecord.demandCategory leaf values an entry matches. A
 * list, because the network ROLLUPS (Nova Network, Alexa Network) aggregate
 * several leaf categories — their `values` are the union of member leaves, per
 * the CTS network_summary / demand_category_summary composition: Nova Network =
 * Nova General + Nova Expert; Alexa Network = Alexa+ + Classic Alexa. Leaf
 * entries carry their single real value. (NOTE: the CTS glossary also folds
 * "Others" into the Alexa Network Summary rollup; we keep Others standalone per
 * the current product decision — revisit if parity requires including it.)
 *
 * When the GetWBR config API ships, this file is the seam that
 * becomes API-driven.
 */

import { NetworkId } from 'src/types';

export interface DemandCategoryDefinition {
  /** URL-facing slug (pre-existing route param), enforced against NetworkId. */
  id: NetworkId;
  /** Display name exactly as in the WBR dashboard. */
  displayName: string;
  /**
   * The value sent as `metricCategory` when authoring a callout in this
   * network's section — the CTS QuickSight VISUAL TITLE the callout is keyed by
   * (CollectionTrackerServiceCDK quicksightStack.ts `DEMAND_CATEGORIES[].name`).
   * Matches CTS exactly so callouts share one vocabulary: network ROLLUPS carry
   * "Summary" ("Nova Network Summary", "Alexa Network Summary"), individual
   * categories don't ("Nova General", "Alexa+", …). Kept SEPARATE from
   * displayName because section titles append " Summary" to displayName and
   * would otherwise double it.
   */
  calloutCategory: string;
  /** DcrRecord.demandCategory values that belong to this entry. */
  values: string[];
  /** Checked by default on the Detailed Summary tab (QuickSight default). */
  defaultSelected: boolean;
}

export const DEMAND_CATEGORIES: DemandCategoryDefinition[] = [
  // Network ROLLUPS: their `values` are the UNION of the real leaf
  // `demand_category` values they aggregate (CTS network_summary /
  // demand_category_summary composition), NOT a literal "Nova Network" value —
  // no DCR carries that. Nova Network = Nova General + Nova Expert; Alexa
  // Network = Alexa+ + Classic Alexa (Others kept standalone), per the CTS
  // glossary + CDK.
  {
    id: 'nova-network',
    displayName: 'Nova Network',
    calloutCategory: 'Nova Network Summary',
    values: ['Nova General', 'Nova Expert'],
    defaultSelected: true,
  },
  {
    id: 'nova-general',
    displayName: 'Nova General',
    calloutCategory: 'Nova General',
    values: ['Nova General'],
    defaultSelected: true,
  },
  {
    id: 'nova-expert',
    displayName: 'Nova Expert',
    calloutCategory: 'Nova Expert',
    values: ['Nova Expert'],
    defaultSelected: true,
  },
  {
    id: 'alexa-network',
    displayName: 'Alexa Network',
    calloutCategory: 'Alexa Network Summary',
    values: ['Alexa+', 'Alexa +', 'Classic Alexa'],
    defaultSelected: true,
  },
  {
    id: 'alexa-plus',
    displayName: 'Alexa+',
    calloutCategory: 'Alexa+',
    values: ['Alexa+', 'Alexa +'],
    defaultSelected: true,
  },
  {
    id: 'classic-alexa',
    displayName: 'Classic Alexa',
    calloutCategory: 'Classic Alexa',
    values: ['Classic Alexa'],
    defaultSelected: false,
  },
  {
    id: 'others',
    displayName: 'Others',
    calloutCategory: 'Others',
    values: ['Others'],
    defaultSelected: false,
  },
];

/** Ordered ids for the checkbox row (registry order = QuickSight order). */
export const DEMAND_CATEGORY_IDS: NetworkId[] = DEMAND_CATEGORIES.map(
  (d) => d.id,
);

/** QuickSight default: the five main entries checked. */
export const DEFAULT_SELECTED_DEMAND_CATEGORIES: NetworkId[] =
  DEMAND_CATEGORIES.filter((d) => d.defaultSelected).map((d) => d.id);

/** slug → display name (incl. the 'all' pseudo-entry). */
export const DEMAND_CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  all: 'All Networks',
  ...Object.fromEntries(DEMAND_CATEGORIES.map((d) => [d.id, d.displayName])),
};

export function demandCategoryDisplayName(id: string): string {
  return DEMAND_CATEGORY_DISPLAY_NAMES[id] ?? id;
}

/**
 * slug → callout `metricCategory` value (the CTS visual title). The 'all'
 * pseudo-entry maps to CTS's all-networks OTD rollup visual, titled "Network".
 */
export const CALLOUT_METRIC_CATEGORIES: Record<string, string> = {
  all: 'Network',
  ...Object.fromEntries(
    DEMAND_CATEGORIES.map((d) => [d.id, d.calloutCategory]),
  ),
};

/**
 * The `metricCategory` to send when authoring a callout in this network's
 * section — matches the CTS QuickSight visual title exactly (CTS parity), so
 * HILT and CTS/WBR-Agent callouts share one vocabulary. Never blank (the
 * backend requires it on create).
 */
export function calloutMetricCategory(id: string): string {
  return CALLOUT_METRIC_CATEGORIES[id] ?? id;
}

/** Does this DCR demandCategory value belong to the given entry? */
export function matchesDemandCategory(
  id: string,
  value: string | undefined,
): boolean {
  if (id === 'all') return true;
  const entry = DEMAND_CATEGORIES.find((d) => d.id === id);
  if (!entry) return false;
  // Normalize (trim) at this single point so every caller — tiles, table
  // sections (dcrsForNetwork), and filters — agrees: a whitespace-padded value
  // like ' Nova General' must not match in one surface and miss in another.
  return entry.values.includes((value ?? '').trim());
}

/**
 * demandCategory value → owning entry id (undefined = unmapped). A leaf value
 * appears both in its own leaf entry and in a rollup's union, so return the
 * MOST SPECIFIC match (smallest `values` list): 'Nova General' → nova-general,
 * never the nova-network rollup; 'Classic Alexa' → classic-alexa, not
 * alexa-network.
 */
export function demandCategoryIdForValue(
  value: string | undefined,
): string | undefined {
  // Trim at this single point, the same normalization matchesDemandCategory
  // applies — otherwise a whitespace-padded value (' Nova General') would match
  // in the tile/section path but return undefined here.
  const v = (value ?? '').trim();
  let best: DemandCategoryDefinition | undefined;
  for (const entry of DEMAND_CATEGORIES) {
    if (
      entry.values.includes(v) &&
      (!best || entry.values.length < best.values.length)
    ) {
      best = entry;
    }
  }
  return best?.id;
}
